import { useEffect } from "react";
import { Form, Formik, useFormikContext } from "formik";

import { type DealGPViewResponseV3 } from "@vega-api/generated/types/dealGPViewResponseV3";
import { type DealOverviewSectionRequest } from "@vega-api/generated/types/dealOverviewSectionRequest";
import { type DealRequestV2 } from "@vega-api/generated/types/dealRequestV2";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { FormikUnsavedChangesModal } from "@vega-core/components/Modals/UnsavedChangesModal/FormikUnsavedChangesModal";
import { type CoreCurrency } from "@vega-core/models/money";
import * as m from "@vega-core/paraglide/messages.js";
import { useDefaultCurrency } from "@vega-core/utils/formatMoney";
import {
  type FormMetric,
  isMoneyValue,
  transformMetricsForForm,
} from "@vega-modules/scenes/CoInvestmentsGP/utils/metricTransforms";

import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";
import { toMoneyWithZeroDefault } from "../../utils/toMoneyWithZeroDefault";
import { getCoInvestmentDetailsValidationSchema } from "../../validations/coInvestmentValidation";

import { BasicInformationSection } from "./components/BasicInformationSection";
import { DealInformationSection } from "./components/DealInformationSection";
import { LpDealOverviewContentSection } from "./components/LpDealOverviewContentSection";
import { TeaserInformationSection } from "./components/TeaserInformationSection";

export type CoInvestmentEditFormData = Omit<DealRequestV2, "dealOverview"> & {
  id: string;
  fundName: string;
  teaserFile?: File;
  currency: CoreCurrency;
  dealOverview?: {
    metrics: FormMetric[];
    sections: DealOverviewSectionRequest[];
  };
};

export type CoInvestmentInlineFormProps = {
  dealResponse: DealGPViewResponseV3;
  onSubmit: (values: CoInvestmentEditFormData) => Promise<void> | void;
  isLoading?: boolean;
  onTeaserUploaded?: () => void;
  canEditDeal?: boolean;
  canEditDealOverview?: boolean;
  isSaving: boolean;
};

// This component is used to sync the deal currency fields with the form currency when currency is changed
const SyncDealCurrencyFields = () => {
  const { values, setFieldValue } =
    useFormikContext<CoInvestmentEditFormData>();

  useEffect(() => {
    if (
      values.targetAmount &&
      values.targetAmount.currency !== values.currency
    ) {
      setFieldValue("targetAmount.currency", values.currency, false);
    }

    if (
      values.totalDealSize &&
      values.totalDealSize.currency !== values.currency
    ) {
      setFieldValue("totalDealSize.currency", values.currency, false);
    }

    if (
      values.fundContributionAmount &&
      values.fundContributionAmount.currency !== values.currency
    ) {
      setFieldValue("fundContributionAmount.currency", values.currency, false);
    }

    values.dealOverview?.metrics.forEach((metric, index) => {
      if (
        isMoneyValue(metric.value) &&
        metric.value.currency !== values.currency
      ) {
        setFieldValue(
          `dealOverview.metrics.${index}.value.currency`,
          values.currency,
          false,
        );
      }
    });
    // Only trigger when values.currency changes
  }, [setFieldValue, values.currency]);

  return null;
};

export const CoInvestmentInlineForm = ({
  dealResponse,
  onSubmit,
  isLoading = false,
  onTeaserUploaded,
  canEditDeal,
  canEditDealOverview,
  isSaving,
}: CoInvestmentInlineFormProps) => {
  const defaultCurrency = useDefaultCurrency();
  const { isMutationLocked } = useCoInvestDealReadOnly();
  const isDealEditable = !isMutationLocked && !!canEditDeal;
  const isDealOverviewEditable = !isMutationLocked && !!canEditDealOverview;
  const { deal } = dealResponse;
  const dealCurrency = deal.currency || defaultCurrency;

  const formMetrics = transformMetricsForForm(
    dealResponse.dealOverview?.metrics || [],
  );

  const initialValues: CoInvestmentEditFormData = {
    id: deal.id,
    name: deal.name,
    description: deal.description,
    assetClass: deal.assetClass,
    currency: dealCurrency,
    fundId: deal.fundId,
    generalPartnerId: deal.generalPartnerId,
    fundName: deal.fundName || "",
    closingDate: deal.closingDate,
    autoClose: deal.autoClose ?? false,
    targetAmount: deal.targetAmount
      ? { ...deal.targetAmount, currency: dealCurrency }
      : deal.targetAmount,
    totalDealSize: deal.totalDealSize
      ? { ...deal.totalDealSize, currency: dealCurrency }
      : deal.totalDealSize,
    fundContributionAmount: deal.fundContributionAmount
      ? { ...deal.fundContributionAmount, currency: dealCurrency }
      : deal.fundContributionAmount,
    teaserFile: undefined,
    region: deal.region,
    sector: deal.sector,
    securityType: deal.securityType,
    dealOverview: {
      metrics: formMetrics.map((metric) =>
        isMoneyValue(metric.value)
          ? { ...metric, value: { ...metric.value, currency: dealCurrency } }
          : metric,
      ),
      sections: dealResponse.dealOverview?.sections || [],
    },
  };

  return (
    <Formik
      initialValues={initialValues}
      key={deal.id} // force re-render when deal id changes; using enableReinitialize instead will mark form as dirty when deal response changes
      onSubmit={async (values, { resetForm }) => {
        await onSubmit(values);
        // The form isn't reinitialised on refetch, so reset to the same
        // empty-to-0 coerced money values that were persisted — otherwise a
        // cleared field stays blank until a full page refresh (VD-28160).
        resetForm({
          values: {
            ...values,
            totalDealSize: toMoneyWithZeroDefault(
              values.totalDealSize,
              values.currency,
            ),
            fundContributionAmount: toMoneyWithZeroDefault(
              values.fundContributionAmount,
              values.currency,
            ),
          },
        });
      }}
      validationSchema={getCoInvestmentDetailsValidationSchema()}
    >
      {({ handleSubmit: formikHandleSubmit, dirty }) => (
        <Form className="space-y-8" onSubmit={formikHandleSubmit}>
          <SyncDealCurrencyFields />

          <BasicInformationSection editable={isDealEditable} />

          <DealInformationSection editable={isDealEditable} />

          <LpDealOverviewContentSection editable={isDealOverviewEditable} />

          <TeaserInformationSection
            dealId={dealResponse.deal.id}
            editable={isDealOverviewEditable}
            onTeaserUploaded={onTeaserUploaded}
            teaserData={dealResponse.teaser}
          />

          {isDealEditable || isDealOverviewEditable ? (
            <div className="flex justify-end my-4">
              <ThemedButton
                dataTestId="save-changes-button"
                disabled={!dirty || isLoading}
                isLoading={isSaving}
                type="submit"
                variant="primary"
              >
                {m.common_saveChanges()}
              </ThemedButton>
            </div>
          ) : null}

          <FormikUnsavedChangesModal />
        </Form>
      )}
    </Formik>
  );
};
